import * as yup from 'yup';

import { parseDate } from '../../dates/parseDate';
import { dateFormatter } from '../../formatters/dateFormatter';
import { ObjectTypeBase } from '../../baseTypes/ObjectTypeBase';
import { PropertiesInterface } from '../Property';
import { TypeContextEnum } from '../TypeContextEnum';
import { Types } from '../Types';
import { TYPE_GROUP_TIME } from '../constants';
import { createShapeFromProperties } from '../createShapeFromProperties';

const properties: PropertiesInterface = {
  startDate: {
    type: 'DateTime',
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'LessThan',
        value: 'endDate',
      },
    ],
  },
  endDate: {
    type: 'DateTime',
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'GreaterThan',
        value: 'startDate',
      },
    ],
  },
};

export class DateTimeRangeType extends ObjectTypeBase {
  group = TYPE_GROUP_TIME;

  name = 'DateTimeRange';

  alwaysValidate = true;

  isFixDateTimeRangeValidationEnabled = false;

  contexts = [
    TypeContextEnum.Block,
    TypeContextEnum.Content,
    TypeContextEnum.Data,
    TypeContextEnum.Properties,
    TypeContextEnum.Metatag,
  ];

  friendlyName = 'Date & Time Range';
  properties = properties;

  get example() {
    return {
      startDate: new Date(),
      endDate: new Date(),
    };
  }

  get default(): any {
    return {
      startDate: null,
      endDate: null,
    };
  }

  parseValue(value: any) {
    const ret: { startDate: Date | null; endDate: Date | null } = {
      startDate: null,
      endDate: null,
    };

    if (value?.startDate) {
      ret.startDate = parseDate(value.startDate);
    }

    if (value?.endDate) {
      ret.endDate = parseDate(value.endDate);
    }

    return ret;
  }

  formatter(range: any, opts: any) {
    if (!range?.startDate || !range?.endDate) {
      return '';
    }

    return `${dateFormatter(
      range.startDate,
      'yyyy-MM-dd h:mm a',
      opts?.timeZone
    )} to ${dateFormatter(range.endDate, 'yyyy-MM-dd h:mm a', opts?.timeZone)}`;
  }

  buildSchema() {
    if (!this.isFixDateTimeRangeValidationEnabled) {
      // Pre-fix behavior. The `properties` shape below attaches Required +
      // LessThan/GreaterThan validators, but TypeBase's stub handlers for
      // LessThan/GreaterThan call `.notRequired()`, silently cancelling the
      // Required validator and skipping any start/end comparison. Kept as
      // the default until FixDateTimeRangeValidation is fully rolled out.
      return yup.object().shape(createShapeFromProperties(properties)).nullable();
    }

    // A DateTimeRange is only mandatory when the property is configured with a
    // Required validator. When required, both dates must be provided. When
    // optional, the range may be left empty — but if either date is set, both
    // must be set (both-or-neither) and end must not fall before start.
    const isRequired = (this.config?.validators ?? []).some(
      validator => validator.name === 'Required' && Boolean(validator.value)
    );

    // `.nullable()` before `.required()` is intentional: a bare yup.date()
    // tries to CAST a null value to a Date first, producing an "Invalid Date"
    // type error instead of our message. Marking the field nullable lets null
    // pass the type/cast layer, so `.required()` then rejects it cleanly with
    // "... is required.".
    const dateShape = isRequired
      ? {
          startDate: yup
            .date()
            .nullable()
            .required('shared.validation.content.dateTimeRange.startRequired'),
          endDate: yup
            .date()
            .nullable()
            .required('shared.validation.content.dateTimeRange.endRequired'),
        }
      : {
          startDate: yup.date().nullable(),
          endDate: yup.date().nullable(),
        };

    return yup
      .object()
      .shape(dateShape)
      .test(
        'both-or-neither',
        'shared.validation.content.dateTimeRange.bothRequired',
        value => {
          const { startDate, endDate } = value ?? {};
          return Boolean(startDate) === Boolean(endDate);
        }
      )
      .test(
        'start-before-end',
        'End date cannot be before start date.',
        value => {
          const { startDate, endDate } = value ?? {};
          if (startDate && endDate) {
            return new Date(startDate) <= new Date(endDate);
          }
          return true;
        }
      )
      .nullable();
  }
}

const definition = new DateTimeRangeType();

Types.addBaseType(definition);
export { definition as DateTimeRangeDefinition };
